/**
 * Translation Helper Functions
 *
 * Helper functions for translating dynamic values like asset categories,
 * sectors, and industries that come from the database.
 *
 * IMPORTANT: These functions use a cached i18n instance that may not be
 * available during initial module load. The functions safely return the
 * input value if translation is not available.
 */

let i18nInstance: any = null;

/**
 * Set the i18n instance (called by i18n/config after initialization)
 */
export function setI18nInstance(i18n: any) {
  i18nInstance = i18n;
}

/**
 * Get the i18n instance for NON-component modules that need to localize a
 * mapped message (e.g. utils/apiError). Deliberately NOT a direct
 * `i18n/config` import: that module pulls react-i18next in at load time,
 * which explodes under test files that mock react-i18next. Null until
 * i18n/config has initialized — callers keep an English defaultValue.
 */
export function getI18nInstance(): any {
  return i18nInstance;
}

/**
 * Get the i18n instance safely
 */
function getI18n() {
  return getI18nInstance();
}

/**
 * Translate an asset type value (e.g., "Stocks" -> "股票" in Chinese)
 */
export function translateAssetType(assetType?: string | null): string {
  if (!assetType) return '';
  const i18n = getI18n();
  if (!i18n) return assetType;
  const key = `assetTypes.${assetType}`;
  return i18n.t(key, { defaultValue: assetType });
}

/**
 * @deprecated Use translateAssetType instead
 */
export function translateAssetCategory(category?: string | null): string {
  return translateAssetType(category);
}

/**
 * The sector vocabulary's codes, and every spelling that resolves to one.
 *
 * Mirrors `wiki/services/sectors.py`, which is the authority. It is duplicated
 * rather than shared because the client has no build step that could import
 * Python, and the surface is small and frozen (r4 C2): eleven GICS sectors plus
 * the explicit unknown. If a sector is ever added, both sides change together.
 */
const SECTOR_CODES: Record<string, string> = {
  // identity: the code, and the GICS display name
  "communication-services": "communication-services",
  "communication services": "communication-services",
  "consumer-discretionary": "consumer-discretionary",
  "consumer discretionary": "consumer-discretionary",
  "consumer-staples": "consumer-staples",
  "consumer staples": "consumer-staples",
  energy: "energy",
  financials: "financials",
  "health-care": "health-care",
  "health care": "health-care",
  industrials: "industrials",
  "information-technology": "information-technology",
  "information technology": "information-technology",
  materials: "materials",
  "real-estate": "real-estate",
  "real estate": "real-estate",
  utilities: "utilities",
  unclassified: "unclassified",
  uncategorized: "unclassified",
  // provider spellings (what `InstrumentFundamental.sector` holds)
  technology: "information-technology",
  tech: "information-technology",
  healthcare: "health-care",
  "consumer cyclical": "consumer-discretionary",
  "consumer defensive": "consumer-staples",
  "basic materials": "materials",
  "financial services": "financials",
  telecom: "communication-services",
  // asset classes: not sectors, so the vocabulary calls them unknown
  cash: "unclassified",
  crypto: "unclassified",
  commodity: "unclassified",
  "fixed income": "unclassified",
  "private equity": "unclassified",
  others: "unclassified",
};

/** Any spelling of a sector -> its code, or null when nothing recognises it. */
export function sectorCode(sector?: string | null): string | null {
  if (!sector) return null;
  const normalised = sector.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  return SECTOR_CODES[normalised] ?? SECTOR_CODES[normalised.replace(/ /g, "-")] ?? null;
}

/**
 * Translate a sector value (e.g., "information-technology" -> "科技" in Chinese).
 *
 * Accepts a code, a GICS name, or a provider spelling, because all three reach
 * this function: the API now emits codes, the position override stores codes, and
 * the instrument reads still carry whatever the provider said. Resolving to a code
 * first is what stops two spellings of one sector rendering differently.
 */
export function translateSector(sector?: string | null): string {
  if (!sector) return '';
  const i18n = getI18n();
  if (!i18n) return sector;
  const code = sectorCode(sector);
  if (code) {
    return i18n.t(`sectors.${code}`, { defaultValue: sector });
  }
  // An unknown sector the vocabulary has never heard of: try the raw key, then
  // show what we were given rather than an empty cell.
  return i18n.t(`sectors.${sector}`, { defaultValue: sector });
}

/**
 * Translate an industry value (e.g., "Semiconductors" -> "半导体" in Chinese)
 */
export function translateIndustry(industry?: string | null): string {
  if (!industry) return '';
  const i18n = getI18n();
  if (!i18n) return industry;
  const key = `industries.${industry}`;
  return i18n.t(key, { defaultValue: industry });
}

/**
 * Translate either position sector or instrument sector with fallback
 */
export function translateSectorWithFallback(
  positionSector?: string | null,
  instrumentSector?: string | null
): string {
  if (positionSector) return translateSector(positionSector);
  if (instrumentSector) return translateSector(instrumentSector);
  return '';
}

/**
 * Translate either position industry or instrument industry with fallback
 */
export function translateIndustryWithFallback(
  positionIndustry?: string | null,
  instrumentIndustry?: string | null
): string {
  if (positionIndustry) return translateIndustry(positionIndustry);
  if (instrumentIndustry) return translateIndustry(instrumentIndustry);
  return '';
}

/**
 * Sanitize instrument names by replacing mojibake characters with regular dashes.
 * Handles UTF-8 em-dashes (U+2014) that may have been misinterpreted.
 */
function sanitizeInstrumentName(name: string | null | undefined): string {
  if (!name) return '';
  return name.replace(/[—–‒]/g, '-').replace(/鈹\?/g, '-');
}

/**
 * Get the localized instrument name based on current language.
 * Falls back to English name if Chinese name is not available.
 *
 * @param companyName - English company name
 * @param nameSch - Simplified Chinese name
 * @param nameTch - Traditional Chinese name
 * @param language - Current language code ('en', 'zh-CN', 'zh-TW')
 * @returns Localized name with fallback to English
 */
export function getLocalizedInstrumentName(
  companyName?: string | null,
  nameSch?: string | null,
  nameTch?: string | null,
  language: string = 'en'
): string {
  if (language === 'zh-CN' && nameSch?.trim()) {
    return sanitizeInstrumentName(nameSch);
  }
  if (language === 'zh-TW' && nameTch?.trim()) {
    return sanitizeInstrumentName(nameTch);
  }
  // Fallback to English for all cases (including 'en')
  return sanitizeInstrumentName(companyName);
}

/**
 * Localized theme name from the wiki registry, falling back to canonical
 * English (spec 006 D3). Accepts non-slug legacy values (sector names, old
 * free-text themes) and returns them unchanged, so CalibrationScreen can
 * render mixed-vocabulary buckets during the transition.
 *
 * Callers must use useTranslation() so language switches re-render; this
 * helper is not reactive on its own.
 */
export function translateTheme(
  slugOrName?: string | null,
  registry?: Map<string, import('../services/api/themeApi').ThemeRegistryEntry> | null,
  language: string = 'en'
): string {
  if (!slugOrName) return '';
  const entry = registry?.get(slugOrName);
  if (!entry) return slugOrName;
  return entry.display_names?.[language]?.trim() || entry.display_name;
}
